import React, { useEffect, useState } from 'react';
import { LogBox, Platform } from 'react-native';
import { MD3LightTheme as defaultTheme, Provider as PaperProvider, type MD3Theme } from 'react-native-paper';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import Toast from 'react-native-toast-message';
import ExpoConstants from 'expo-constants';
import { StatusBar } from 'expo-status-bar';
import { PortalProvider } from '@gorhom/portal';
import * as Sentry from '@sentry/react-native';
import AppUpdateDialog from 'src/presentation/dialogs/AppUpdateDialog';
import ExerciseDialog from 'src/presentation/dialogs/ExerciseDialog';
import LeaveAppDialog from 'src/presentation/dialogs/LeaveAppDialog';
import { useApiSettingsStore } from 'src/domain/stores/useApiSettingsStore';
import { useFeaturedItemStore } from 'src/domain/stores/useFeaturedItemStore';
import { useLoyaltyStore } from 'src/domain/stores/useLoyaltyStore';
import { useWorkspacesStore } from 'src/domain/stores/useWorkspacesStore';
import useCachedResources from 'src/presentation/hooks/useCachedResources';
import Navigation from 'src/presentation/navigation/Navigation';
import Colors from 'src/core/utils/Colors';
import Constants from 'src/core/utils/Constants';
import { apiUrl, environmentName } from 'src/core/utils/AppConfig';
import Updater from 'src/presentation/helpers/Updater';
import { toastConfig } from 'src/presentation/configurations/ToastConfiguration';
import { useGlobalStore } from 'src/domain/stores/useGlobalStore';
import { useAuthStore } from 'src/domain/stores/useAuthStore';
import { useServicesStore } from 'src/domain/stores/useServicesStore';
import type ApiRequestError from 'src/core/models/api/ApiRequestError';

const TAG = 'APP ->';
const ENVIRONMENT: string = environmentName() ?? Constants.environmentDevelopment;

// Only the environments with real users; the rest would drown production in noise.
const SENTRY_ENABLED_ENVIRONMENTS: string[] = [Constants.environmentAcceptance, Constants.environmentProduction];

const CONVERT_DISABLED_ENVIRONMENTS: string[] = [Constants.environmentLocal, Constants.environmentDevelopment];

console.info(`${TAG} ENVIRONMENT:`, ENVIRONMENT);
console.info(`${TAG} URL:`, apiUrl());
console.info(`${TAG} VERSION:`, ExpoConstants.expoConfig?.version);

const theme: MD3Theme = {
	...defaultTheme,
	roundness: 4,
	colors: {
		...defaultTheme.colors,
		primary: Colors.green,
		error: Colors.primary,
	},
};

Sentry.init({
	debug: __DEV__,
	dsn: Constants.sentryDsn,
	enabled: SENTRY_ENABLED_ENVIRONMENTS.includes(ENVIRONMENT),
	environment: ENVIRONMENT,
	release: ExpoConstants.expoConfig?.version,
	tracesSampleRate: 0,
	_experiments: {
		profilesSampleRate: 0,
	},
	ignoreErrors: [
		'AxiosError', // Axios will throw all kind of errors when network requests fail or when an timeout occurs.
		'Rage Click',
		'(evaluating ',
		'Unable to open URL', // https://gitlab.cube.nl/cube/hexagon/thermen-resorts/applicaties/-/issues/2883
		'UnknownError: Database deleted by request of the user', // https://gitlab.cube.nl/cube/hexagon/thermen-resorts/applicaties/-/issues/3041
		'App Hanging', // o&b
		'App hanging for at least 2000 ms', // https://gitlab.cube.nl/cube/hexagon/thermen-resorts/applicaties/-/issues/3042
		'UnknownError: An internal error was encountered in the Indexed Database server', // https://gitlab.cube.nl/cube/hexagon/thermen-resorts/applicaties/-/issues/3046
		'The object can not be found here.', // https://gitlab.cube.nl/cube/hexagon/thermen-resorts/applicaties/-/issues/3047
		'com.facebook.infer.annotation.Assertions in assertCondition', // No exception message available, so will add the sentry issue title here. https://gitlab.cube.nl/cube/hexagon/thermen-resorts/applicaties/-/issues/3043
		'undefined is not an object (evaluating', // https://gitlab.cube.nl/cube/hexagon/thermen-resorts/applicaties/-/issues/3052
		'The database connection is closing.', // https://cube-nl.sentry.io/issues/5799728808/?environment=production&project=4506579644514305&query=is%3Aunresolved%20issue.priority%3A%5Bhigh%2C%20medium%5D&referrer=issue-stream&statsPeriod=90d&stream_index=12
		'Network Error', // https://gitlab.cube.nl/cube/hexagon/thermen-resorts/applicaties/-/issues/3250#note_742854
		'6000ms timeout exceeded', // https://gitlab.cube.nl/cube/hexagon/thermen-resorts/applicaties/-/issues/3250#note_742854
		'Non-Error promise rejection captured with value: Not implemented on this platform', // https://gitlab.cube.nl/cube/hexagon/thermen-resorts/applicaties/-/issues/3250#note_742854
		'ResizeObserver loop completed with undelivered notifications.', // https://gitlab.cube.nl/cube/hexagon/thermen-resorts/applicaties/-/issues/3250#note_742854
		'HTTPClientError', // https://gitlab.cube.nl/cube/hexagon/thermen-resorts/applicaties/-/issues/3397
		'HTTP Client Error with status code', // https://gitlab.cube.nl/cube/hexagon/thermen-resorts/applicaties/-/issues/3397
		'CanceledError', // https://gitlab.cube.nl/cube/hexagon/thermen-resorts/applicaties/-/issues/3397
		'Request call failed', // https://gitlab.cube.nl/cube/hexagon/thermen-resorts/applicaties/-/issues/3397
		'Request failed with status code', // https://gitlab.cube.nl/cube/hexagon/thermen-resorts/applicaties/-/issues/3397
		'ReferenceError', // https://gitlab.cube.nl/cube/hexagon/thermen-resorts/applicaties/-/issues/3337
		"Can't find variable: f", // https://gitlab.cube.nl/cube/hexagon/thermen-resorts/applicaties/-/issues/3337
		"Cannot read properties of undefined (reading 'routes", // https://gitlab.cube.nl/cube/hexagon/thermen-resorts/applicaties/-/issues/3337
		't.addEventListener is not a function', // https://gitlab.cube.nl/cube/hexagon/thermen-resorts/applicaties/-/issues/3337
		'Installations: Could not process request. Application offline.', // https://gitlab.cube.nl/cube/hexagon/thermen-resorts/applicaties/-/issues/3337
		'App hanging for at least 2000 ms', // https://gitlab.cube.nl/cube/hexagon/thermen-resorts/applicaties/-/issues/3337
		'AssertionError', // https://gitlab.cube.nl/cube/hexagon/thermen-resorts/applicaties/-/issues/3337
		'/^abort/i', // https://gitlab.cube.nl/cube/nexus/thermen-resorts/applicaties/-/issues/4224
		'e.addEventListener is not a function', // https://gitlab.cube.nl/cube/nexus/thermen-resorts/applicaties/-/issues/4223
	],
	denyUrls: [
		/rum-static\.pingdom\.net\/.*\.js$/,
		/consent\.cookiebot\.com\/.*\.js$/,
		/googletagmanager\.com\/.*\.js$/,
		/static\.zdassets\.com\/.*\.js$/,
		/edge\.marker\.io\/.*\.js$/,
		/cdn-4\.convertexperiments\.com\/.*\.js$/,
		/cdn\.seamly-app\.com\/.*\.js$/,
	],
	beforeBreadcrumb(breadcrumb) {
		// Don't add breacrumbs with the `console` category so that we have more readable stack traces.
		if (breadcrumb.category === 'console') {
			return null;
		}
		return breadcrumb;
	},
	beforeSend(event) {
		// Only log events that have the level `fatal` or `error` since these are the most important ones.
		if (event.level === 'fatal' || event.level === 'error') {
			return event;
		}
		return null;
	},
});

// One Convert project for every environment, so acceptance reports into the production experiments.
if (Platform.OS === Constants.platformWeb && !CONVERT_DISABLED_ENVIRONMENTS.includes(ENVIRONMENT)) {
	const script = document.createElement('script');
	script.type = 'text/javascript';
	script.src = '//cdn-4.convertexperiments.com/js/10042571-10044557.js';
	document.head.appendChild(script);
}

export default function App() {
	const isLoadingComplete = useCachedResources();

	const { isAuthenticated } = useAuthStore();
	const { fetchWorkspaces, isResortPickerVisible } = useWorkspacesStore();
	const { selectedBenefit, resortPickerCard } = useLoyaltyStore();
	const { selectedItem } = useFeaturedItemStore();
	const { setInitOrigin } = useGlobalStore();
	const { setAnalyticsCollectionEnabled, initializeFacebook, getI18n, captureEvent } = useServicesStore();

	const { apiSettings, fetchApiSettings, canShowAppUpdateDialog } = useApiSettingsStore();
	const [isAppUpdateDialogVisible, setAppUpdateDialogVisible] = useState(false);
	const [isAppUpdateDialogShown, setAppUpdateDialogShown] = useState(false);

	const language = getI18n().language;

	useEffect(() => {
		if (Platform.OS === Constants.platformAndroid || Platform.OS === Constants.platformIos) {
			// Without this the native platform reports `App measurement is disabled`.
			setAnalyticsCollectionEnabled(true);

			// Set up facebook.
			initializeFacebook();
		}
	}, [initializeFacebook, setAnalyticsCollectionEnabled]);

	useEffect(() => {
		// Both stores render their own failure, so the catch only nets a throw inside that handling.
		if (isAuthenticated) {
			fetchWorkspaces().catch((error: ApiRequestError) => {
				captureEvent({ message: 'Fetching the workspaces on app start failed.', extra: { error } });
			});
		}

		fetchApiSettings().catch((error: ApiRequestError) => {
			captureEvent({ message: 'Fetching the api settings on app start failed.', extra: { error } });
		});

		// #2697: Added origin to track the origin of the user on web variant
		if (Platform.OS === Constants.platformWeb) {
			const urlParams = new URLSearchParams(window.location.search);

			if (urlParams.has(Constants.paramsOrigin)) {
				setInitOrigin(urlParams.get(Constants.paramsOrigin) as string);
			}
		}
	}, [captureEvent, fetchApiSettings, fetchWorkspaces, isAuthenticated, language, setInitOrigin]);

	useEffect(() => {
		if (!isAppUpdateDialogShown && canShowAppUpdateDialog()) {
			setAppUpdateDialogShown(true);
			setAppUpdateDialogVisible(true);
		}
	}, [apiSettings, canShowAppUpdateDialog, isAppUpdateDialogShown]);

	LogBox.ignoreLogs(['Sending...']);

	const isLeaveAppHostedBySheet =
		isResortPickerVisible || selectedBenefit !== null || resortPickerCard !== null || selectedItem !== null;

	if (!isLoadingComplete) {
		return null;
	} else {
		return (
			<SafeAreaProvider>
				<Updater />
				{apiSettings !== null && (
					<AppUpdateDialog
						apiSettings={apiSettings}
						isDialogVisible={isAppUpdateDialogVisible}
						onCancelPress={() => setAppUpdateDialogVisible(false)}
					/>
				)}
				<PaperProvider theme={theme}>
					<PortalProvider>
						<StatusBar style={'light'} translucent={true} />
						<Navigation />
						<ExerciseDialog />
						{/* A sheet hosts its own copy, so this one stays out of its way. */}
						{isLeaveAppHostedBySheet ? null : <LeaveAppDialog />}
						<Toast config={toastConfig} />
					</PortalProvider>
				</PaperProvider>
			</SafeAreaProvider>
		);
	}
}
